import {
  Database, Layers,
  FlaskConical, Search, TrendingUp, ClipboardCheck, Tags, Radio, ShieldCheck,
  Lightbulb,
} from "lucide-react";
import type { FeatureCategory } from "./types";

export const featureCategories: FeatureCategory[] = [
  {
    key: "ab-test",
    label: "A/B Tests",
    icon: FlaskConical,
    description: "Axis scoring, zone parsing, hypothesis framing, and variant generation for A/B tests.",
    promptKeyPrefix: "ab-test",
    backendAis: [
      { name: "Product Name Extractor", trigger: "Runs on drawer open", description: "Vision model reads the Amazon hero image and extracts the canonical product name from packaging. Output is treated as ground truth by every downstream prompt." },
      { name: "Signal AI Rationale", trigger: "Runs after Variant B is generated", description: "Composes the short rationale banner shown above Variant B explaining what changed and why, grounded in the active hypothesis and lever choices." },
      { name: "Zone Keyword Integration", trigger: "Runs in Alternatives panel", description: "Rewrites a single zone to integrate a target keyword phrase without disturbing neighboring zones, used when the user explores keyword-placement alternatives." },
    ],
    validatorTitle: "Validator Checks  —  A/B Tests",
  },
  {
    key: "seo-insights",
    label: "SEO Insights",
    icon: Search,
    description: "Thematic clustering, strategic briefs, and recommendation prioritization for SEO.",
    promptKeyPrefix: "seo-insights",
    validatorTitle: "Validator Checks  —  SEO Insights",
  },
  {
    // AUT-223: the three SEO Strategy prompts are grouped separately from SEO Insights.
    // Prompts are matched to a category by feature_key prefix, so "seo-strategy" must be
    // its own entry or seo-strategy-* prompts would never appear on this page.
    key: "seo-strategy",
    label: "Strategy",
    icon: Lightbulb,
    description: "Content Scorecard gap consolidation, thematic grouping for a gap, and the recommendation cards on the Strategy tab.",
    promptKeyPrefix: "seo-strategy",
    validatorTitle: "Validator Checks  —  Strategy",
    configSettings: [
      {
        alias: "seo_strategy_gap_threshold",
        label: "Gap Threshold",
        description:
          "Content Scorecard questions scoring below this become eligible Strategy recommendations. 80 matches the existing \"strong\" cutoff used elsewhere in scoring.",
        default: 80,
      },
    ],
  },
  {
    key: "rank-tracker",
    label: "Rank Tracker",
    icon: TrendingUp,
    description: "Movement detection, ranking deltas, and competitive shift signals.",
    promptKeyPrefix: "rank-tracker",
    validatorTitle: "Validator Checks  —  Rank Tracker",
  },
  {
    key: "scorecard",
    label: "Content Scorecard",
    icon: ClipboardCheck,
    description: "COSMO-aligned listing audit with evidence-backed scores and recommendations.",
    promptKeyPrefix: "scorecard",
    validatorTitle: "Validator Checks  —  Content Scorecard",
  },
  {
    key: "search-terms",
    label: "Search Terms",
    icon: Tags,
    description: "Backend search term set generation, deduplication, and overlap checks.",
    promptKeyPrefix: "search-terms",
    validatorTitle: "Validator Checks  —  Search Terms",
  },
  {
    key: "ai-review",
    label: "Brand Signals",
    icon: Radio,
    description: "MYE report parsing, hypothesis extraction, and vote weighting for the signal pipeline.",
    promptKeyPrefix: "ai-review",
    validatorTitle: "Validator Checks  —  Brand Signals",
  },
  {
    key: "keyword-themes",
    label: "Keyword Themes",
    icon: Layers,
    description: "Thematic keyword grouping suggestions, per-theme keyword fill, and theme AI summaries.",
    promptKeyPrefix: "theme",
    validatorTitle: "Validator Checks  —  Keyword Themes",
    configSettings: [
      {
        alias: "theme_min_search_volume",
        label: "Minimum Search Volume",
        description: "Keywords below this monthly search volume are excluded from theme suggestions and theme keyword fill.",
        default: 100,
      },
      {
        alias: "theme_min_relevance",
        label: "Minimum Relevance Score",
        description: "Keywords below this relevance score are excluded.",
        default: 60,
      },
      {
        alias: "theme_min_kps",
        label: "Minimum KPS per Keyword",
        description: "Keywords below this KPS are excluded. Currently only enforced by the AI's own filtering rules, not a hard pre-filter (see 5a's PHP pre-filter, which only applies search volume/relevance).",
        default: 50,
      },
      {
        alias: "theme_keywords_per_theme",
        label: "Keywords per Theme",
        description: "Target tail-keyword count per theme (plus 1 head term). A target, not a requirement -- the AI will return fewer if the remaining candidates aren't strong enough.",
        default: 9,
      },
      {
        alias: "theme_keyword_bank_cap",
        label: "Keyword Bank Cap",
        description: "Maximum number of filtered keywords sent to the model when generating theme and theme-keyword suggestions, sorted by relevance then search volume. Raising this lets more of a large keyword bank reach the model, at the cost of a larger prompt.",
        default: 200,
      },
    ],
  },
];

export const sharedValidatorCategory: FeatureCategory = {
  key: "shared-validator",
  label: "Validator",
  icon: ShieldCheck,
  description: "Cross-cutting validation prompt and check logic. Changes here propagate to all AI features.",
  promptKeyPrefix: "validator",
  validatorTitle: "Check Categories  —  Active features per check",
};

export const allCategories = [...featureCategories, sharedValidatorCategory];

export const payloadLibraryNavItem = {
  key: "payload-library" as const,
  label: "Payload Library",
  icon: Database,
};

export const SEVERITY_LABELS: Record<"hard_fail" | "warning", string> = {
  hard_fail: "Hard Fail",
  warning: "Warning",
};

/**
 * Resolves a `feature_name` to its owning `FeatureCategory` via a `promptKeyPrefix`
 * `startsWith` join, or `null` for an unmatched prefix (the known `brand-signals` orphan).
 *
 * NOTE: prefixes are verified non-overlapping today, so a plain `.find` is safe; a future
 * overlapping prefix would need longest-match-first ordering instead.
 */
function resolveFeatureCategory(featureName: string): FeatureCategory | null {
  return featureCategories.find((category) => featureName.startsWith(category.promptKeyPrefix)) ?? null;
}

/** Human category label for a `feature_name`, falling back to the raw `featureName`. */
export function resolveFeatureCategoryLabel(featureName: string): string {
  return resolveFeatureCategory(featureName)?.label ?? featureName;
}

/** Category key for a `feature_name`, falling back to the raw `featureName`. */
export function resolveFeatureCategoryKey(featureName: string): string {
  return resolveFeatureCategory(featureName)?.key ?? featureName;
}
